import { defineComponent as _defineComponent } from 'vue'
import { useRouter } from '@nuxtjs/composition-api';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { computed, defineAsyncComponent, getCurrentInstance, nextTick, onMounted, ref, watch } from 'vue';

// Helpers
import {
  APP_HERO_PRODUCTS,
  PROGRESS_INDICATOR_PAGES,
} from '@white-label-configuration/constants';
import {
  DATE_TIME_FORMATS,
  format,
  getTripDurationInDays,
} from '@white-label-helper/date-utilities';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { searchPollingHelper } from '@white-label-helper/polling-helper-search';
import { useScreenSize, useCountryOptions } from '@white-label-helper/shared-composables';

// Types
import type VueI18n from 'vue-i18n';

// Components
import {
  CavuButton,
  IconWrapper,
  LoadingScreen,
  ModalCenter,
} from 'ui-shared-components';
import IconS3 from '@white-label-icon/icons-s3';

import TravelInsuranceFormStep1 from '../travel-insurance-form-step-1/travel-insurance-form-step-1.vue';
import TravelInsuranceFormStep2 from '../travel-insurance-form-step-2/travel-insurance-form-step-2.vue';
import TravelInsuranceFormStep3 from '../travel-insurance-form-step-3/travel-insurance-form-step-3.vue';

// Store
import { ACTION_METRIC, addCustomMetric, CONTEXT_KEYS, METRIC_TYPE } from '@white-label-helper/shared-helpers';
import { useCartStore } from '@white-label-store/cart';
import { useSearchResultsStore } from '@white-label-store/search-results';
import { useTravelInsuranceStore, type Traveller } from '@white-label-store/travel-insurance';


export default /*#__PURE__*/_defineComponent({
  __name: 'travel-insurance-home-page',
  setup(__props, { expose }) {

// Packages
const router = useRouter()
const instance = getCurrentInstance();
const proxy = instance?.proxy as Vue;

const cartStore = useCartStore();
const travelInsuranceStore = useTravelInsuranceStore();
const searchResultsStore = useSearchResultsStore();
const { isMobileResolution } = useScreenSize();
const { getCountryById } = useCountryOptions();

const closeErrorModal = () => {
  searchResultsStore.updateSearchResultsErrors(null);
}

const searchResultsErrors = computed(() => {
  if (!searchResultsStore.searchResultsErrors?.travel_insurance) return null;
  const { code, response: rawResponse } = searchResultsStore.searchResultsErrors.travel_insurance;

  let response: string;
  if (typeof rawResponse === 'string') {
    response = rawResponse;
  } else if (typeof rawResponse === 'object' && rawResponse !== null) {
    const messages = Object.values(rawResponse).flat().filter(Boolean);
    response = messages.length > 0 ? messages.join(' ') : 'An error occurred while fetching the quote.';
  } else {
    response = 'An error occurred while fetching the quote.';
  }

  const destinationListMatcher = response.match(/:\s*(.+)$/);
  let destinationList: string[] = [];
  if (code === 406 && destinationListMatcher) {
    destinationList = destinationListMatcher[1].split(',').map(code => code.trim());
    response = response.replace(/(:)\s*.+$/, '$1');
  }

  return {
    code,
    response,
    destinationList,
    ctaText: code === 406
      ? proxy.$t('UI.modalError.travelInsurance.button') as string
      : proxy.$t('shared.buttons.tryAgain') as string,
    callback: code === 406
      ? closeErrorModal
      : () => {
        closeErrorModal();
        viewQuote();
      },
  };
});

const quoteLoading = ref(false);

let lastSentStartDateEvent: Date | string = '';
let lastSentEndDateEvent: Date | string = '';
let sentTravellersDOBEvent: {id: string, dob: Date | string}[] = [];

const pageTitle = computed((): string | VueI18n.TranslateResult => {
  return getAppVariable<string | VueI18n.TranslateResult>(
    `page_title[${proxy.$i18n.locale}]`,
    proxy.$t('searchBox.title')
  );
});

const formStep1Ref = ref<InstanceType<typeof TravelInsuranceFormStep1> | null>(null);
const formStep2Ref = ref<InstanceType<typeof TravelInsuranceFormStep2> | null>(null);
const formStep3Ref = ref<InstanceType<typeof TravelInsuranceFormStep3> | null>(null);
const formData = ref({
  destination: [] as string[],
  travelDates: {
    startDate: '' as Date | string,
    endDate: '' as Date | string,
  },
  travellers: [] as Traveller[],
});

const defaultCountryId = computed<string>(() => 'GB');
const defaultCountry = computed(() => getCountryById(defaultCountryId.value));

const searchProducts = searchPollingHelper().searchProducts;

async function viewQuote() {
  formStep1Ref.value?.validateForm();
  formStep2Ref.value?.validateForm();
  formStep3Ref.value?.submitTravellers();
  travellersIsValid.value = formStep3Ref.value?.validateTravellers() ?? false;

  if (!formIsValid.value) {
    return;
  }
  //to clear a cart from any previous quote and avoid stale data issues
  cartStore.commitClearCart();
  quoteLoading.value = true;
  try {
    travelInsuranceStore.updateFormField('destination', formData.value.destination);
    travelInsuranceStore.updateFormField('travelDates', formData.value.travelDates);
    travelInsuranceStore.updateFormField('travellers', formData.value.travellers.map(traveller => {
      if (traveller.isPrimary) {
        traveller.countryOfResidence = defaultCountry.value; // Set primary traveller's country of residence to default country as for MVP launch only the UK insurance product will be available
      }
      return traveller;
    }));
    travelInsuranceStore.selectedAddOnIds = [];

    const results = await searchProducts({ ...travelInsuranceStore.form }, APP_HERO_PRODUCTS.TRAVEL_INSURANCE, proxy.$i18n.locale);

    if (searchResultsErrors.value) {
      throw new Error(searchResultsErrors.value.response);
    } else if (results.length === 0) {
      // Ideally integrations service should always return some error message if no results are found.
      // In the rare case it doesn't, create a generic error message
      searchResultsStore.updateSearchResultsErrors({
        travel_insurance: {
          code: 404,
          response: proxy.$t('searchExtras.travelInsurance.errorMsg.noResults') as string,
        }
      });
      throw new Error(searchResultsErrors.value!.response);
    }

    travelInsuranceStore.setQuoteResults(results);
    router.push({path: PROGRESS_INDICATOR_PAGES.quote});
  } catch (error) {
    console.error('Error occurred while fetching the travel insurance quote.', error);
  } finally {
    quoteLoading.value = false;
  }
}

const travellersIsValid = ref(false);
function handleTravellersUpdate(updatedTravellers: Traveller[]) {
  formData.value.travellers = updatedTravellers;
  nextTick(() => {
    travellersIsValid.value = formStep3Ref.value?.validateTravellers() ?? false;
  });

  updatedTravellers.forEach(traveller => {
    if (!traveller.dateOfBirth) return;
    const alreadySent = sentTravellersDOBEvent.find(sent => sent.id === traveller.id && isEqual(sent.dob, traveller.dateOfBirth));
    if (!alreadySent) {
      addCustomMetric(METRIC_TYPE.TRACKING, ACTION_METRIC.TRAVEL_INSURANCE_TRAVELLER_DOB, { dateOfBirth: format(new Date(traveller.dateOfBirth), DATE_TIME_FORMATS.year_month_day) }, null, CONTEXT_KEYS.TRAVEL_INSURANCE_FORM);

      const existingIndex = sentTravellersDOBEvent.findIndex(sent => sent.id === traveller.id);
      if (existingIndex !== -1) {
        sentTravellersDOBEvent[existingIndex] = { id: traveller.id, dob: traveller.dateOfBirth };
      } else {
        sentTravellersDOBEvent.push({ id: traveller.id, dob: traveller.dateOfBirth });
      }
    }
  });
}

const formIsValid = computed (() => {
  return formData.value.destination.length > 0
    && !!(formData.value.travelDates.startDate && formData.value.travelDates.endDate)
    && travellersIsValid.value;
})

const tripDuration = computed(() => {
  const { startDate, endDate } = formData.value.travelDates;
  return getTripDurationInDays(startDate, endDate);
});

watch(tripDuration, () => {
  nextTick(() => {
    travellersIsValid.value = formStep3Ref.value?.validateTravellers() ?? false;
  });
});

const saveDates = (dates: { startDate: Date | string; endDate: Date | string }) => {
  formData.value.travelDates.startDate = dates.startDate ?? '';
  formData.value.travelDates.endDate = dates.endDate ?? '';
}

const trackStartAndEndDates = (dateType: 'start' | 'end', date: Date | string) => {
  if (dateType === 'start') {
    if (isEqual(date, lastSentStartDateEvent)) return;
    lastSentStartDateEvent = date;
    addCustomMetric(METRIC_TYPE.TRACKING, ACTION_METRIC.TRAVEL_INSURANCE_TRIP_START, { date: format(new Date(date), DATE_TIME_FORMATS.year_month_day) }, null, CONTEXT_KEYS.TRAVEL_INSURANCE_FORM);
  }

  if (dateType === 'end') {
    if (isEqual(date, lastSentEndDateEvent)) return;
    lastSentEndDateEvent = date;
    addCustomMetric(METRIC_TYPE.TRACKING, ACTION_METRIC.TRAVEL_INSURANCE_TRIP_END, { date: format(new Date(date), DATE_TIME_FORMATS.year_month_day) }, null, CONTEXT_KEYS.TRAVEL_INSURANCE_FORM);
  }
}

const trackTravellingToDestinations = () => {
  const destinations = formData.value.destination;
  if (destinations.length === 0) return;
  addCustomMetric(METRIC_TYPE.TRACKING, ACTION_METRIC.TRAVEL_INSURANCE_TRAVELLING_TO, { data: destinations }, null, CONTEXT_KEYS.TRAVEL_INSURANCE_FORM);
}

onMounted(() => {
  formData.value = cloneDeep(travelInsuranceStore.form);
});

watch(
  () => formData.value,
  (newValues) => {
    if (!isEqual(newValues, travelInsuranceStore.form)) {
      travelInsuranceStore.form = cloneDeep(newValues);
    }
  },
  { deep: true },
)

expose({
  isValid: formIsValid
})

return { __sfc: true,router, instance, proxy, cartStore, travelInsuranceStore, searchResultsStore, isMobileResolution, getCountryById, closeErrorModal, searchResultsErrors, quoteLoading, lastSentStartDateEvent, lastSentEndDateEvent, sentTravellersDOBEvent, pageTitle, formStep1Ref, formStep2Ref, formStep3Ref, formData, defaultCountryId, defaultCountry, searchProducts, viewQuote, travellersIsValid, handleTravellersUpdate, formIsValid, tripDuration, saveDates, trackStartAndEndDates, trackTravellingToDestinations, CavuButton, IconWrapper, LoadingScreen, ModalCenter, IconS3, TravelInsuranceFormStep1, TravelInsuranceFormStep2, TravelInsuranceFormStep3 }
}

})